import { watch } from 'vue';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useOnboardingStore } from '@/stores/onboarding';
import { routeAllowed, VITRINE_ROUTE_STAGES } from '@/onboardingAccess';
import {
  currentProduct, productFromAccessToken, productHome, useProduct, type Product,
} from '@/product';
import { sharedRoutes } from './routes.shared';
import { vitrinePrivateRoutes } from './routes.vitrine';
import { fullPrivateRoutes } from './routes.full';
import { installStaleChunkReload } from './staleChunkReload';
import { handoffToken } from '@/magicHandoff';

/**
 * Single-bundle, runtime-`ctype` router (Phase 2.2). Seeded with SHARED public routes only; the
 * authenticated product's PRIVATE routes are installed at runtime (router.addRoute → lazy chunks) once
 * the product is known from the JWT ctype. Only one product's routes are ever live per session, so the
 * colliding names (login/settings/history/…) never clash and a foreign path falls to the catch-all →
 * the current product's home. Switching product (logout → other-product login) swaps route sets.
 */

/** Landing redirect for `/` and unknown paths: login when anonymous, else the product home. */
function landing() {
  const auth = useAuthStore();
  if (!auth.isAuthenticated) return { name: 'login' as const };
  // Resolve the product from the reactive/persisted hint, falling back to the JWT ctype — the localStorage
  // hint can be absent on a cookie-refresh cold boot, and default to 'full' so a product is always known.
  // CRITICAL: install that product's private routes BEFORE returning productHome, otherwise the redirect
  // target (e.g. {name:'card'}) has no matching route yet and vue-router throws during router.install()
  // → the whole app fails to mount (blank screen for an already-logged-in client on refresh).
  const p = currentProduct() ?? productFromAccessToken(auth.accessToken) ?? 'full';
  installProductRoutes(p);
  return productHome(p);
}

const routes: RouteRecordRaw[] = [
  ...sharedRoutes,
  { path: '/', redirect: landing },
  { path: '/:catchAll(.*)', name: 'catch-all', redirect: landing },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

installStaleChunkReload(router);

// ── Dynamic per-product route installation ─────────────────────────────────
let installed: { product: Product; removers: Array<() => void> } | null = null;

function installProductRoutes(p: Product): void {
  if (installed?.product === p) return;
  if (installed) { installed.removers.forEach(remove => remove()); installed = null; }
  const records = p === 'vitrine' ? vitrinePrivateRoutes : fullPrivateRoutes;
  installed = { product: p, removers: records.map(rec => router.addRoute(rec)) };
}

function uninstallProductRoutes(): void {
  if (installed) { installed.removers.forEach(remove => remove()); installed = null; }
}

// Install/swap the product's routes whenever the resolved product changes (login, refresh, logout).
// immediate:true installs from the persisted hint on cold boot; flush:'sync' guarantees the routes
// exist synchronously after setTokens()→setProduct(), before a login view's router.push({name:'card'}).
const { product } = useProduct();
watch(product, p => { p ? installProductRoutes(p) : uninstallProductRoutes(); }, { immediate: true, flush: 'sync' });

// ── Guard ──────────────────────────────────────────────────────────────────
router.beforeEach(async to => {
  const auth = useAuthStore();

  if (to.meta?.public) {
    // Never show the login screen to an already-authenticated client — send them to their product.
    if (to.name === 'login' && auth.isAuthenticated) return productHome(currentProduct());
    return true;
  }

  if (!auth.isAuthenticated) return { name: 'login' };

  // Authenticated + private target: make sure this product's routes are installed, then re-resolve.
  const p = currentProduct() ?? productFromAccessToken(auth.accessToken);
  if (p && installed?.product !== p) {
    installProductRoutes(p);
    return to.fullPath;
  }

  if (p === 'vitrine' && VITRINE_ROUTE_STAGES[String(to.name)]) {
    const onboarding = useOnboardingStore();
    // Race the probe: on a slow link this would otherwise block navigation for the full HTTP timeout and the
    // client would stare at nothing. Whatever we know after ~1.5s decides; unknown stays permissive, and the
    // destination screen handles its own card-less state anyway.
    await Promise.race([onboarding.load(), new Promise(r => setTimeout(r, 1500))]);
    if (!routeAllowed(String(to.name), onboarding.stage)) return { name: 'card' };
  }
  // Keep the magic token in every address during the hand-off window, so "Open in Safari" from any section
  // signs Safari in too (see magicHandoff.ts). Signed-in pages only: a login screen never carries it.
  const handoff = p === 'vitrine' ? handoffToken() : null;
  // No `replace: true`: the redirect keeps the navigation's own kind. Forcing a replace turned every in-app push of
  // the first ten minutes after a magic sign-in into a replace of the page before it, so «Back» from a chat opened
  // on the issuance screen skipped that screen (full-cjm-video step 9, 01.11).
  if (handoff && to.query.token !== handoff) {
    return { path: to.path, query: { ...to.query, token: handoff }, hash: to.hash };
  }
  return true;
});
